import {
    buildAddressClean,
    buildStreetWithHouseNumber,
    type GeoRefAddress,
    type ILatLonCoordinate,
} from "@reva/shared/geo-reference";
import {type RevaValueParameterType, SEGMENT} from "@reva/shared/reva-spec";
import type {ValuationData} from "@reva/shared/valuation-data";
import type {RevaValuationPurpose} from "@reva/shared/valuation-purpose";
import equal from "fast-deep-equal";

import {
    AssignUserApiInterface,
    SearchAssignableUsersApiInterface,
    SearchFinalizeAssignableUsersApiInterface,
} from "../api/interface/assignable-users";
import {FinalizeApiInterface} from "../api/interface/finalize";
import {ChangePurposeApiInterface} from "../api/interface/persistence-change-purpose";
import {PersistenceCorrectionApiInterface} from "../api/interface/persistence-correction";
import {DeleteValuationAndOrderApiInterface, DeleteValuationApiInterface} from "../api/interface/persistence-delete";
import {PERSISTENCE_NOT_FOUND_ERROR_KEY, PersistenceFetchApiInterface} from "../api/interface/persistence-fetch";
import {PersistenceFetchSnapshotApiInterface} from "../api/interface/persistence-fetch-snapshot";
import {PersistenceImportFromOrdersApiInterface} from "../api/interface/persistence-import-from-orders";
import {PersistenceInitializationApiInterface} from "../api/interface/persistence-initialization";
import {
    type AbstractPersistApiInterface,
    type AbstractPersistSingleEntryApiInterface,
    PersistFullReplaceParametersApiInterface,
    PersistMultipleDocumentPropertiesApiInterface,
    PersistMultipleParametersApiInterface,
    PersistSingleDocumentPropertyApiInterface,
    PersistSingleParameterApiInterface,
} from "../api/interface/persistence-parameter";
import {PreFinalizeApiInterface} from "../api/interface/pre-finalize";
import {SearchValuationsAtAddressApiInterface} from "../api/interface/search-valuations-at-address";
import type {DeleteValuationResponse} from "../api/payload-types/persistence-delete";
import type {ArchiveDocumentType, FinalizeValuationResponse} from "../api/payload-types/persistence-finalize";
import type {ValueIndicationRequest, ValueIndicationResponse} from "../api/payload-types/value-indication";
import {ErrorI18n} from "../i18n";
import {generateDefaultQueryError, type QueryError} from "../util/errorUtil";
import {entryPurposeForPath, resolveCreationPurpose} from "../util/purposeResolution";
import {RevaPermission} from "../util/revaPermission";
import {buildValuationId, parseValuationId} from "../util/valuationId";
import {clearAddress, duplicateValuationsFound, purposeChoiceRequired} from "./addressSlice";
import {persistenceAddressLoaded, removeDistinctAddress} from "./addressThunk";
import {selectPermissionCheck} from "./authSelectors";
import {selectCredentials, selectPermissions} from "./authSlice";
import {loadDocumentsAsync} from "./documentsThunk";
import {abortRunningIndications} from "./indicationRequests";
import {selectCurrentIndicationPayload} from "./indicationSelectors";
import {loadLocationInfosAsync} from "./locationInfoThunk";
import {handleNotificationsFromPersistence} from "./notificationsSlice";
import {selectHasFirstFinalizeData, selectVisibleObjectParameters} from "./objectParametersSelectors";
import {
    selectAllDocProperties,
    selectAllEnergyParams,
    selectAllIdpEnergyCertParams,
    selectAllImmodropParams,
    selectAllInspectionDerivedParams,
    selectAllParams,
    selectAllPlotParams,
    selectAllRegisterParams,
    selectSegment,
    setInitialValuationAndInspectionParams,
    setSegmentAndParameters,
    setSingleParam,
} from "./objectParametersSlice";
import {validateAndSubmitThunk} from "./objectParametersThunk";
import {DOC_PROPERTY_GROSS_FLOOR_AREA_MODEL, DOC_PROPERTY_POIS_PER_TYPE} from "./pdfConstants";
import {selectHasPersistenceErrorForSomeParam} from "./persistenceSelectors";
import type {AssignableUser} from "./persistenceSlice";
import {
    beginPersistenceWrite,
    beginSegmentWrite,
    endPersistenceWrite,
    endSegmentWrite,
    nextPersistenceRequestSeq,
    selectLastAppliedResponseSeq,
    selectLastValidPersistenceResponse,
    selectLoadedSnapshotNumber,
    selectPendingWrites,
    selectResyncAfterWritesFromSeq,
    selectRevaId,
    setAssignableUsers,
    setAssignableUsersLoading,
    setFinalizationError,
    setFinalizationLoading,
    setInitialized,
    setPersistenceLoading,
    setPersistenceLoadingFinished,
    setPersistenceSingleParamError,
    setPersistenceSingleParamLoading,
    setPreFinalizeError,
    setPreFinalizeLoading,
    setQueryError,
    setReloadError,
    setReloadLoading,
    setResyncAfterWritesFromSeq,
    setRevaId,
    setUpdatedValuationResponse,
} from "./persistenceSlice";
import type {RevaThunkAction} from "./revaHooks";
import {importSectionIIFromInitialValuation} from "./sectionIIThunk";
import {importSpecialPropertyFeatures} from "./specialPropertyFeaturesThunk";
import type {RevaDispatch, RevaExtraArgs} from "./store";
import {selectFlattenedIndicationData} from "./valueIndicationSelectors";
import {selectIndicationData, selectRequestData, setIndicationData} from "./valueIndicationSlice";
import {queryValueIndicationWithCurrentParamsAsync} from "./valueIndicationThunk";
import {setValueOrders} from "./valueOrdersSlice";

/** Options of {@link initializePersistence}. */
export interface InitializePersistenceOptions {
    /** skip the duplicate-address lookup and create directly (default false) */
    skipDuplicateCheck?: boolean;
    /** purpose of the new REVA ID; without it the purpose of the entry URL applies */
    purpose?: RevaValuationPurpose;
}

/**
 * Async thunk to create a new valuation in the persistence.
 *
 * #18907: By default this first checks whether the user's license already holds valuations at the same
 * address (coordinate + street) and, if so, opens the duplicate-selection dialog instead of creating right
 * away (see below). Pass `{skipDuplicateCheck: true}` to bypass that check and create directly - used when
 * the user has already confirmed "create new anyway" in the dialog, and for machine-driven import flows.
 * The check is skipped for revision-readonly users (reva-lifecycle-revision-readonly) as well, as they must
 * not create valuations at all. Without a purpose permission the notice from {@link resolveCreationPurpose}
 * applies, otherwise the server rejects the creation.
 *
 * #19481: After the duplicate check, the purpose of the new REVA ID is determined (see
 * {@link resolveCreationPurpose}): `purpose` or else the entry URL (`/start/research`, `/start/valuation`)
 * dictates it, otherwise the permissions decide - with more than one allowed purpose the selection dialog
 * opens and calls this thunk again with the chosen purpose. Without any of those permissions nothing is
 * created and a notice is shown instead.
 * @param addressCandidate distinct (found) address candidate
 * @param options see {@link InitializePersistenceOptions}
 */
export const initializePersistence =
    (
        addressCandidate: GeoRefAddress,
        {skipDuplicateCheck = false, purpose}: InitializePersistenceOptions = {},
    ): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, extraArgument) => {
        const {serviceApi} = extraArgument;

        if (!addressCandidate) {
            console.info("No address candidate found");
            return;
        }

        // #18907: Revision-Readonly-User (reva-lifecycle-revision-readonly) dürfen ohnehin keine
        // Bewertungen anlegen — der Doppler-Dialog wäre für sie eine Sackgasse. Check überspringen; ohne
        // Zweck-Recht greift dann der Hinweis unten (#19481), sonst weist der Server die Neuanlage ab.
        const isRevisionReadOnly = selectPermissionCheck(RevaPermission.REVA_LIFECYCLE_REVISION_READONLY)(getState());

        // #18907: Before creating a new valuation, check whether the user's license already holds valuations
        // at the same address (coordinate + street incl. house number, unsplit — the backend splits it
        // canonically). If any exist, open the duplicate-selection dialog and return instead of creating.
        // The check is a convenience step and must never prevent creating a valuation: if the lookup itself
        // fails (network/5xx/…), we swallow the error and fall through to the normal creation path (fail-open).
        if (!skipDuplicateCheck && !isRevisionReadOnly) {
            try {
                const result = await new SearchValuationsAtAddressApiInterface({
                    coordinate: {lat: addressCandidate.lat, lon: addressCandidate.lon},
                    street: buildStreetWithHouseNumber(addressCandidate.street, addressCandidate.houseNumber),
                }).execute(serviceApi, selectCredentials(getState())!);

                if (result?.matches?.length) {
                    // Existing valuations found → let the user pick one or confirm the new one via the dialog.
                    dispatch(duplicateValuationsFound({matches: result.matches, pendingAddress: addressCandidate}));
                    return;
                }
            } catch (err) {
                // Convenience-only: a failed lookup must not block creating a valuation — fall through.
                console.warn("Duplicate-address check failed, creating a new valuation directly", err);
            }
        }

        // #19481: Die Einstiegs-URL wird hier gelesen statt von jedem Aufrufer (Suchfeld, ?search=, Adressauswahl,
        // Doppler-Dialog, Exposé-/JSON-Import) durchgereicht — sie alle laufen auf der Startseite.
        const purposeDecision = resolveCreationPurpose(
            selectPermissions(getState()),
            purpose ?? entryPurposeForPath(window.location.pathname),
        );
        if (purposeDecision.kind === "forbidden") {
            dispatch(
                setQueryError(generateDefaultQueryError(new ErrorI18n("purpose.noCreatePermission", null, false))),
            );
            dispatch(clearAddress());
            return;
        }
        if (purposeDecision.kind === "ask") {
            dispatch(purposeChoiceRequired({pendingAddress: addressCandidate, options: purposeDecision.options}));
            return;
        }

        const state = getState();
        const segment = selectSegment(state);
        const parameters = selectAllParams(state);
        const documentProperties = selectAllDocProperties(state);
        const registerParameters = selectAllRegisterParams(state);
        const energyCertificateParameters = selectAllEnergyParams(state);
        const plotParameters = selectAllPlotParams(state);
        const inspectionDerivedParameters = selectAllInspectionDerivedParams(state);
        const immodropParameters = selectAllImmodropParams(state);

        if (!parameters) {
            console.info("No parameters found");
            return;
        }
        // Remove "segment" from REVA parameters object, as in the API it is a distinct property.
        const {segment: _, ...revaParams} = parameters as Record<string, unknown>;

        const apiInterface = new PersistenceInitializationApiInterface(
            buildAddressClean(addressCandidate),
            addressCandidate,
            segment,
            revaParams,
            documentProperties,
            registerParameters,
            energyCertificateParameters,
            plotParameters,
            inspectionDerivedParameters,
            immodropParameters,
            purposeDecision.purpose,
        );

        dispatch(setPersistenceLoading());

        const credentials = selectCredentials(state);
        try {
            const response = await apiInterface.execute(serviceApi, credentials!);

            dispatch(handleValidPersistenceResponseReceived(response));

            dispatch(setPersistenceLoadingFinished());
        } catch (err) {
            dispatch(setQueryError(generateDefaultQueryError(err as Error)));
            // Reset address (otherwise address would show as loading indefinitely)
            dispatch(clearAddress());
        }
    };

/**
 * Async thunk to request a valuation from persistence and return it as a Promise.
 * "Internal": this does NOT mutate the Redux store (no dispatch of the loaded data) — callers decide
 * what to do with the result.
 *
 * An id of the form `<RevaId>.<snapshotNumber>` (or `:`) loads the frozen snapshot instead of the
 * live valuation (see {@link parseValuationId}); the response is treated like a regular valuation.
 * @param id unique identifier of the valuation to load. Typically a RevaId, but the endpoint also
 *   resolves other kinds of unique valuation identifiers (e.g. an external order number or a value order GUID).
 * @param forceUpdateExternalData flag, if external data (like Value-Orders, Inspection-Protocols) should always be refreshed
 */
export const fetchPersistenceByIdInternal =
    (id: string, forceUpdateExternalData?: boolean | null): RevaThunkAction<Promise<ValuationData>> =>
    async (dispatch, getState, extraArgument) => {
        const {serviceApi} = extraArgument;
        const credentials = selectCredentials(getState());
        const {id: valuationId, snapshotNumber} = parseValuationId(id);
        const apiInterface =
            snapshotNumber !== null
                ? new PersistenceFetchSnapshotApiInterface(valuationId, snapshotNumber)
                : new PersistenceFetchApiInterface(valuationId, forceUpdateExternalData ?? null);
        return await apiInterface.execute(serviceApi, credentials!);
    };

/**
 * Async thunk to load a valuation from persistence.
 * Variant 1: provide parameter "id" and load this is.
 * Variant 2: provide parameters "orderId" and "clientId" and try to load or import this Order from ValueAG Order-API.
 *
 * #19481: Darf der User mehrere Zwecke anlegen und gibt `purpose` keinen vor, wird bei Variante 2 erst geladen: Hat der
 * Auftrag schon eine REVA-ID, öffnet sie sich ohne Rückfrage, sonst fragt der Auswahldialog nach dem Zweck und ruft
 * danach mit `purpose` erneut auf. Mit genau einem Zweck-Recht entscheidet wie bisher der Server.
 * @param id revaId or orderId or externalOrderNumber to fetch (variant 1)
 * @param orderId orderId to fetch or import (variant 2)
 * @param clientId clientId to fetch or import (variant 2)
 * @param purpose Zweck, falls der Auftrag neu importiert wird (variant 2)
 */
export const fetchPersistence =
    ({
        id,
        orderId,
        clientId,
        purpose,
    }: {
        id?: string;
        orderId?: string;
        clientId?: string;
        purpose?: RevaValuationPurpose;
    }): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, extraArgument) => {
        const {serviceApi} = extraArgument;

        dispatch(setPersistenceLoading());

        const credentials = selectCredentials(getState());
        // Vor dem Request ziehen, damit ein Schreibvorgang, der SPÄTER abgeschickt wurde, das
        // Ergebnis dieses Ladevorgangs überschreiben darf und nicht umgekehrt.
        const seq = nextPersistenceRequestSeq();
        try {
            let response: ValuationData | undefined;

            if (id) {
                // Variant 1: load existing valuation from ID (revaId or orderId or externalOrderNumber)
                response = await fetchPersistenceByIdInternal(id)(dispatch, getState, extraArgument);
            } else if (orderId && clientId) {
                const purposeDecision = purpose ? null : resolveCreationPurpose(selectPermissions(getState()), null);
                if (purposeDecision?.kind === "ask") {
                    response = await fetchPersistenceByIdIfExists(orderId)(dispatch, getState, extraArgument);
                    if (!response) {
                        dispatch(setPersistenceLoadingFinished());
                        dispatch(setInitialized());
                        dispatch(
                            purposeChoiceRequired({
                                pendingOrder: {orderId, clientId},
                                options: purposeDecision.options,
                            }),
                        );
                        return;
                    }
                } else {
                    // Variant 2: try to load valuation by orderId, and if not found try to import the order from ValueAG Orders API
                    const apiInterface = new PersistenceImportFromOrdersApiInterface(orderId, clientId, purpose);
                    response = await apiInterface.execute(serviceApi, credentials!);
                }
            }

            dispatch(handleValidPersistenceResponseReceived(response!, seq));

            dispatch(setPersistenceLoadingFinished());
        } catch (err) {
            dispatch(removeDistinctAddress());
            dispatch(setQueryError(generateDefaultQueryError(err as Error)));
        }
    };

/**
 * Lädt eine REVA-ID wie {@link fetchPersistenceByIdInternal}, liefert aber `undefined`, wenn es keine gibt (#19481:
 * Vorabprüfung, ob ein Auftrag schon importiert ist). Andere Fehler werden weitergeworfen.
 * @param id REVA-ID oder Auftragsnummer/Value-Order-GUID
 */
const fetchPersistenceByIdIfExists =
    (id: string): RevaThunkAction<Promise<ValuationData | undefined>> =>
    async (dispatch, getState, extraArgument) => {
        try {
            return await fetchPersistenceByIdInternal(id)(dispatch, getState, extraArgument);
        } catch (err) {
            if (err instanceof ErrorI18n && err.message === PERSISTENCE_NOT_FOUND_ERROR_KEY) return undefined;
            throw err;
        }
    };

/**
 * Übernimmt die Parameterschichten einer Bewertungsantwort in den Store: das effektive Segment, die
 * REVA-Overrides und alle Unterschichten samt ihren Kommentaren.
 *
 * Wird nicht nur beim Laden gebraucht: seit #19444 entscheidet das Backend, welche Schichten ein
 * Segmentwechsel verwirft, und dieselbe Antwort kann auch an einem Schreibvorgang hängen (Exposé-Import,
 * Segment-PATCH). Der Aufrufer stellt sicher, dass die Antwort nicht von einer neueren überholt wurde.
 * @param response die Bewertungsantwort
 */
export const applyValuationParametersFromResponse =
    (response: ValuationData): RevaThunkAction<void> =>
    (dispatch) => {
        dispatch(
            setSegmentAndParameters({
                segment: response.segment,
                params: response.parametersInReva,
                comments: response.parameterOverrideComments,
                properties: response.documentProperties,
                plot: response.parametersInPlot,
                plotComments: response.parameterPlotComments,
                inspectionDerived: response.parametersInInspectionDerived,
                inspectionDerivedComments: response.parameterInspectionDerivedComments,
                energy: response.parametersInEnergyCertificate,
                energyComments: response.parameterEnergyCertificateComments,
                idpEnergyCertificate: response.parametersInIdpEnergyCertificate,
                register: response.parametersInRegister,
                registerComments: response.parameterRegisterComments,
                immodrop: response.parametersInImmodrop,
            }),
        );
    };

/**
 * Handle steps to be taken after a valid persistence response was received from loading or creating a valuation.
 * @param response the valuation data received
 * @param seq Sequenznummer der zugehörigen Anfrage, mit {@link nextPersistenceRequestSeq} VOR dem
 *        Absenden gezogen. Ohne Angabe wird sie erst hier gezogen — die Antwort gilt dann als die
 *        neueste und verdrängt alle noch fliegenden Schreib-Antworten. Das ist für Vorgänge richtig,
 *        die den Stand autoritativ neu setzen ((Vor-)Finalisieren, Wiedereröffnen, Anlegen); für ein
 *        reines Laden/Neuladen sollte die Nummer vor dem Request gezogen und hier durchgereicht werden.
 */
export const handleValidPersistenceResponseReceived =
    (response: ValuationData, seq?: number): RevaThunkAction<void> =>
    (dispatch, getState) => {
        const effectiveSeq = seq ?? nextPersistenceRequestSeq();
        // Überholte Antwort komplett verwerfen. Der Guard im Reducer schützt nur
        // lastValidPersistenceResponse; Adresse, revaId, Value-Orders und die Parameter-Slices
        // würden sonst weiter aus der alten Antwort gesetzt und der Store wäre in sich
        // widersprüchlich (Beispiel: Bewertung A langsam laden, sofort B öffnen, A trifft zuletzt ein).
        if (effectiveSeq < selectLastAppliedResponseSeq(getState())) {
            return;
        }

        // #19374: Ab hier wird der Store auf die geladene Bewertung umgestellt. Eine noch laufende
        // Indikation gehört zum vorigen Stand — ihre Antwort würde sonst per setIndicationData die
        // gerade gesetzten (bei Snapshots eingefrorenen) Werte überschreiben.
        abortRunningIndications();

        dispatch(
            persistenceAddressLoaded(
                response.addressQuery,
                response.address,
                response.hasReferenceAddress,
                response.referenceAddressComment,
                response.originalAddress,
            ),
        );

        dispatch(setRevaId(response.revaId));

        dispatch(setUpdatedValuationResponse({response, seq: effectiveSeq}));
        // Die Parameter werden hier ohnehin vollständig aus der Antwort gesetzt (#19774).
        dispatch(setResyncAfterWritesFromSeq(null));

        dispatch(setValueOrders(response.valueOrders ?? []));

        dispatch(applyValuationParametersFromResponse(response));

        dispatch(
            setInitialValuationAndInspectionParams({
                initialValuationParams: response.parametersInInitialValuation,
                inspectionParams: response.parametersInInspection,
                initialValuationOutputParams: response.parametersOutInitialValuation,
                initialValuationAdditionalProperties: response.initialValuationAdditionalProperties,
                firstFinalizeParams: response.parametersInFirstFinalize,
            }),
        );

        dispatch(loadLocationInfosAsync());

        if (response.notifications) {
            void dispatch(handleNotificationsFromPersistence(response.notifications));
        }

        if (response.isSnapshot) {
            // Snapshot: eingefrorene Indikations-Daten direkt aus den Snapshot-Parametern vorbefüllen,
            // statt die Indikationen neu auszuführen. Das echte Ausführen ist im readonly-Zustand
            // (Snapshot oder Final) zusätzlich über die Thunk-Gates gesperrt.
            dispatch(
                setIndicationData({
                    requestData: (response.snapshotParametersInValuation || {}) as ValueIndicationRequest,
                    indicationData: (response.snapshotParametersOutValuation || {}) as ValueIndicationResponse,
                }),
            );
        } else {
            dispatch(importSectionIIFromInitialValuation());
            dispatch(importSpecialPropertyFeatures());
            dispatch(validateAndSubmitThunk());
        }
    };

/**
 * Reload the CURRENTLY loaded valuation without invalidating it.
 *
 * Unlike {@link fetchPersistence} (which loads a *new* valuation by id and, on failure, discards the
 * loaded state via removeDistinctAddress/setQueryError), this refreshes the valuation that is already
 * open: on success it pushes the fresh data into the store via {@link handleValidPersistenceResponseReceived};
 * on failure it only records a reload error (setReloadError) and leaves the loaded valuation untouched.
 *
 * Snapshot-aware: reloads the same snapshot when one is open (see {@link buildValuationId}).
 * @param forceUpdateExternalData bypass the cache and re-pull external data (Value-Orders, Inspection-Protocols)
 */
export const reloadPersistence =
    ({forceUpdateExternalData}: {forceUpdateExternalData?: boolean} = {}): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, extraArgument) => {
        const state = getState();
        const revaId = selectRevaId(state);
        if (!revaId) {
            return;
        }
        const id = buildValuationId(revaId, selectLoadedSnapshotNumber(state));

        dispatch(setReloadLoading(true));
        const seq = nextPersistenceRequestSeq();
        try {
            const response = await fetchPersistenceByIdInternal(id, forceUpdateExternalData)(
                dispatch,
                getState,
                extraArgument,
            );
            dispatch(handleValidPersistenceResponseReceived(response, seq));
            dispatch(setReloadLoading(false));
        } catch (err) {
            // Keep the currently loaded valuation intact; only surface the reload error.
            dispatch(setReloadError(generateDefaultQueryError(err as Error)));
        }
    };

/**
 * Async thunk to persist a single parameter.
 * @param parameter parameter name
 * @param parameterType REVA parameter type
 * @param value new value
 * @param comment optional comment
 */
export const persistSingleParameter =
    (
        parameter: string,
        parameterType: RevaValueParameterType,
        value: unknown,
        comment?: string | null,
    ): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, {serviceApi}) => {
        const revaId = selectRevaId(getState());
        const credentials = selectCredentials(getState());

        let apiInterface: AbstractPersistSingleEntryApiInterface;
        switch (parameterType) {
            case "DOC_PROPERTY":
                apiInterface = new PersistSingleDocumentPropertyApiInterface(revaId, parameter, value);
                break;
            case "AVM":
            case "ENERGY_CERT":
            case "IDP_ENERGY_CERTIFICATE":
            case "REGISTER":
            case "INSPECTION_DERIVED":
            case "PLOT":
                apiInterface = new PersistSingleParameterApiInterface(parameterType, revaId, parameter, value, comment);
                break;
            default:
                // META, FIRST_FINALIZE and unknown types cannot be persisted — keep prior behavior: warn and bail out.
                console.warn(
                    `persistSingleParameter: unsupported parameterType ${parameterType} for parameter ${parameter}`,
                );
                return;
        }

        if (
            !apiInterface.validateAndDispatchQueryError((queryError) =>
                dispatch(setPersistenceSingleParamError(queryError)),
            )
        ) {
            return;
        }

        dispatch(setPersistenceSingleParamLoading(true));
        const seq = nextPersistenceRequestSeq();
        dispatch(beginPersistenceWrite());

        try {
            const response = await apiInterface.execute(serviceApi, credentials!);

            dispatch(setUpdatedValuationResponse({response, seq}));

            dispatch(setPersistenceSingleParamLoading(false));
        } catch (err) {
            if (!(err instanceof ErrorI18n) || err.message !== "object.error.missingComment") {
                void dispatch(resetPersistenceToLastValidResponse());
            }
            dispatch(setPersistenceSingleParamError(generateDefaultQueryError(err as Error)));
        } finally {
            dispatch(finishPersistenceWrite());
        }
    };

/**
 * Async thunk to persist multiple parameters of the same type in one request.
 * @param params object of parameter name → value
 * @param parameterType REVA parameter type
 * @param comments optional object of parameter name → comment
 */
export const persistMultipleParameters =
    (
        params: Record<string, unknown>,
        parameterType: RevaValueParameterType,
        comments?: Record<string, string | null>,
    ): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, {serviceApi}: RevaExtraArgs) => {
        const revaId = selectRevaId(getState());
        const credentials = selectCredentials(getState());

        let apiInterface: AbstractPersistApiInterface;
        switch (parameterType) {
            case "AVM":
            case "ENERGY_CERT":
            case "IDP_ENERGY_CERTIFICATE":
            case "REGISTER":
            case "INSPECTION_DERIVED":
            case "PLOT": {
                const entries = Object.entries(params).map(([parameter, parameterValue]) => {
                    // Im Backend werden für Kommentare `null` und `undefined` gleich behandelt, also weglassen
                    const comment = comments?.[parameter];
                    return comment != null
                        ? {parameter, parameterValue, comment}
                        : {
                              parameter,
                              parameterValue,
                          };
                });
                apiInterface = new PersistMultipleParametersApiInterface(parameterType, revaId, entries);
                break;
            }
            case "IMMODROP":
                apiInterface = new PersistFullReplaceParametersApiInterface(parameterType, revaId, params);
                break;
            default:
                // META, FIRST_FINALIZE, DOC_PROPERTY and unknown types cannot be persisted here — warn and bail out.
                console.warn(`persistMultipleParameters: unsupported parameterType ${parameterType}`);
                return;
        }

        if (
            !apiInterface.validateAndDispatchQueryError((queryError) =>
                dispatch(setPersistenceSingleParamError(queryError)),
            )
        ) {
            return;
        }

        dispatch(setPersistenceSingleParamLoading(true));
        const seq = nextPersistenceRequestSeq();
        dispatch(beginPersistenceWrite());
        try {
            const response = await apiInterface.execute(serviceApi, credentials!);
            dispatch(setUpdatedValuationResponse({response, seq}));

            // Das Ersetzen der Exposé-Schicht kann das effektive Segment verschieben, wenn keine
            // stärkere Quelle eines vorgibt — dann verwirft das Backend die Overrides und meldet es
            // (#19444). Beides muss hier ankommen, sonst zeigt die Maske weiter den alten Stand.
            if (parameterType === "IMMODROP" && seq >= selectLastAppliedResponseSeq(getState())) {
                dispatch(applyValuationParametersFromResponse(response));
                if (response.notifications) {
                    void dispatch(handleNotificationsFromPersistence(response.notifications));
                }
            }

            dispatch(setPersistenceSingleParamLoading(false));
        } catch (err) {
            dispatch(setPersistenceSingleParamError(generateDefaultQueryError(err as Error)));
        } finally {
            dispatch(finishPersistenceWrite());
        }
    };

/**
 * Async thunk to persist multiple document properties in one request.
 * @param properties object of property name → value
 */
export const persistMultipleDocumentProperties =
    (properties: Record<string, unknown>): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, {serviceApi}) => {
        const revaId = selectRevaId(getState());
        const credentials = selectCredentials(getState());

        const entries = Object.entries(properties).map(([property, value]) => ({
            property,
            propertyValue: value ?? null,
        }));
        const apiInterface = new PersistMultipleDocumentPropertiesApiInterface(revaId, entries);

        if (
            !apiInterface.validateAndDispatchQueryError((queryError) =>
                dispatch(setPersistenceSingleParamError(queryError)),
            )
        ) {
            return;
        }

        dispatch(setPersistenceSingleParamLoading(true));
        const seq = nextPersistenceRequestSeq();
        dispatch(beginPersistenceWrite());
        try {
            const response = await apiInterface.execute(serviceApi, credentials!);
            dispatch(setUpdatedValuationResponse({response, seq}));
            dispatch(setPersistenceSingleParamLoading(false));
        } catch (err) {
            dispatch(setPersistenceSingleParamError(generateDefaultQueryError(err as Error)));
        } finally {
            dispatch(finishPersistenceWrite());
        }
    };

/**
 * Beendet einen Schreibvorgang (Gegenstück zu beginPersistenceWrite). Ist danach keiner mehr unterwegs und
 * wurde währenddessen zurückgesetzt, wird erneut auf die neueste Antwort zurückgesetzt, siehe
 * {@link resetPersistenceToLastValidResponse}.
 */
const finishPersistenceWrite = (): RevaThunkAction<void> => (dispatch, getState) => {
    dispatch(endPersistenceWrite());

    const state = getState();
    const resyncFromSeq = selectResyncAfterWritesFromSeq(state);
    // Wartet ein Feld auf eine Entscheidung (Kommentardialog nach R500), steht dort die abgelehnte Eingabe,
    // die „Bestätigen“ noch braucht. Bestätigen (neuer Schreibvorgang) oder Abbrechen (neues Zurücksetzen)
    // löst die Marke dann auf.
    if (resyncFromSeq == null || selectPendingWrites(state) > 0 || selectHasPersistenceErrorForSomeParam(state)) {
        return;
    }
    dispatch(setResyncAfterWritesFromSeq(null));
    if (selectLastAppliedResponseSeq(state) > resyncFromSeq) {
        void dispatch(resetPersistenceToLastValidResponse());
    }
};

/**
 * Setzt die Parameter lokal auf die letzte gültige Antwort zurück (abgelehnte Eingabe, „Abbrechen“ im
 * Kommentardialog, Fehler beim Speichern).
 *
 * Laufen dabei noch andere Schreibvorgänge, kennt dieser Stand deren Änderungen nicht, und ihre Antworten
 * werden – wie alle Einzel-PATCH-Antworten – nicht auf die Maske angewendet. Deshalb merkt sich das
 * Zurücksetzen den Antwortstand; sobald alle Schreibvorgänge zurück sind, setzt
 * {@link finishPersistenceWrite} auf die dann neueste Antwort zurück (#19774). Ruft ein Schreibvorgang dies
 * aus seinem eigenen Fehlerzweig auf, zählt er selbst noch mit; sein Ende löst dann höchstens ein zweites,
 * inhaltsgleiches Zurücksetzen aus.
 */
export const resetPersistenceToLastValidResponse = (): RevaThunkAction<Promise<void>> => async (dispatch, getState) => {
    const lastValidPersistenceResponse = selectLastValidPersistenceResponse(getState());

    if (!lastValidPersistenceResponse || Object.keys(lastValidPersistenceResponse).length === 0) {
        return;
    }

    dispatch(
        setResyncAfterWritesFromSeq(
            selectPendingWrites(getState()) > 0 ? selectLastAppliedResponseSeq(getState()) : null,
        ),
    );

    // Wie beim Laden: laufende Indikationen abbrechen, bevor der Parameterstand ersetzt wird (#19374).
    abortRunningIndications();

    dispatch(applyValuationParametersFromResponse(lastValidPersistenceResponse));

    if (lastValidPersistenceResponse.isSnapshot) {
        // Snapshot: eingefrorene Indikations-Daten direkt aus den Snapshot-Parametern vorbefüllen,
        // statt die Indikationen neu auszuführen (analog zum Laden). Das echte Ausführen ist im
        // readonly-Zustand (Snapshot oder Final) zusätzlich über die Thunk-Gates gesperrt.
        dispatch(
            setIndicationData({
                requestData: (lastValidPersistenceResponse.snapshotParametersInValuation ||
                    {}) as ValueIndicationRequest,
                indicationData: (lastValidPersistenceResponse.snapshotParametersOutValuation ||
                    {}) as ValueIndicationResponse,
            }),
        );
    } else {
        dispatch(validateAndSubmitThunk());
    }
};

/** Ergebnis der Konsistenzprüfung vor dem (Vor-)Finalisieren, siehe {@link ensureIndicationIsCurrent}. */
export type IndicationCurrency = "current" | "recalculated" | "failed";

/**
 * #19370: Stellt vor dem Einfrieren sicher, dass die im Store liegende AVM-Antwort zum aktuellen Parametersatz
 * gehört. Verglichen wird der tatsächlich gesendete Request mit dem Payload, der jetzt gebaut würde. Weichen
 * beide ab (z.B. Miteigentumsanteil aus dem Grundbuch geändert, ohne dass eine Neuberechnung lief), wird neu
 * gerechnet.
 * @return "current" wenn Antwort und Parameter zusammenpassen, "recalculated" wenn neu gerechnet wurde und die
 *         Antwort jetzt passt, "failed" wenn auch nach der Neuberechnung keine passende Antwort vorliegt
 */
export const ensureIndicationIsCurrent =
    (): RevaThunkAction<Promise<IndicationCurrency>> => async (dispatch, getState) => {
        const isCurrent = () => equal(selectRequestData(getState()), selectCurrentIndicationPayload(getState()));

        if (isCurrent()) {
            return "current";
        }
        const valueResult = await dispatch(queryValueIndicationWithCurrentParamsAsync());
        if (isCurrent()) {
            return "recalculated";
        }
        // #19374: Der Nutzer hat während der Neuberechnung weitergetippt, die neuere Indikation hat unsere
        // abgebrochen. Kein Fehler — die neueren Werte kommen gleich, der Nutzer soll sie ansehen und erneut
        // finalisieren. Deshalb "recalculated" statt "failed" (letzteres meldet einen Defekt mit Kontakt-Link).
        if (valueResult === "aborted") {
            return "recalculated";
        }
        return "failed";
    };

/**
 * Fehlermeldung für ein abgebrochenes (Vor-)Finalisieren, weil die Bewertung nicht zum aktuellen Parametersatz
 * passte (#19370). Nach einer erfolgreichen Neuberechnung soll der Nutzer die Werte erst sehen und dann erneut
 * finalisieren, statt dass ein Wert eingefroren wird, den er nie gesehen hat.
 */
export const outdatedIndicationQueryError = (currency: Exclude<IndicationCurrency, "current">): QueryError =>
    currency === "recalculated"
        ? {error: "general.error.indicationRecalculated", errorHasContactLink: false}
        : {error: "general.error.indicationRecalculationFailed", errorHasContactLink: true};

/**
 * Async thunk to finalize the persistence.
 * The requested documents are generated and archived server-side by REVA-REST.
 * @param documentTypes documents to generate and archive now (empty array = none)
 * @return the response on success, null on error
 */
export const finalizePersistence =
    (documentTypes: ArchiveDocumentType[] = []): RevaThunkAction<Promise<FinalizeValuationResponse | null>> =>
    async (dispatch, getState, extraArgument) => {
        const {serviceApi} = extraArgument;

        // #19370: nie eine AVM-Antwort einfrieren, die nicht zu den aktuellen Parametern gehört
        const indicationCurrency = await dispatch(ensureIndicationIsCurrent());
        if (indicationCurrency !== "current") {
            dispatch(setFinalizationError(outdatedIndicationQueryError(indicationCurrency)));
            return null;
        }

        const revaId = selectRevaId(getState());
        const segment = selectSegment(getState());
        const revaParams = selectAllParams(getState());
        const indicationData = selectIndicationData(getState());
        const valuationParamsIn = selectCurrentIndicationPayload(getState());

        if (!revaParams || !indicationData || !revaId) {
            return null;
        }

        const apiInterface = new FinalizeApiInterface(
            revaId,
            segment,
            revaParams,
            indicationData,
            valuationParamsIn,
            documentTypes,
        );

        if (!apiInterface.validateAndDispatchQueryError((queryError) => dispatch(setFinalizationError(queryError)))) {
            return null;
        }

        dispatch(setFinalizationLoading(true));

        const credentials = selectCredentials(getState());

        try {
            const response = await apiInterface.execute(serviceApi, credentials!);

            dispatch(handleValidPersistenceResponseReceived(response));
            reloadDocumentsAfterArchiving(dispatch, response);

            return response;
        } catch (err) {
            dispatch(setFinalizationError(generateDefaultQueryError(err as Error)));
            return null;
        }
    };

/**
 * Reload the document list in the background when a (pre-)finalization archived documents in the order,
 * so the documents page does not keep showing a state without them.
 *
 * Deliberately a full reload instead of splicing `archivedDocuments` into the list: whoever else added a
 * document in the meantime would otherwise be missing from it — either the whole list or nothing.
 */
function reloadDocumentsAfterArchiving(dispatch: RevaDispatch, response: FinalizeValuationResponse): void {
    if (response.archivedDocuments.length === 0) return;
    void dispatch(loadDocumentsAsync());
}

/**
 * Async thunk to pre-finalize the persistence (creates a PreFinal snapshot without marking the valuation as final).
 * The requested documents are generated and archived server-side by REVA-REST.
 * @param comment
 * @param documentTypes documents to generate and archive now (empty array = none)
 * @return the response on success, null on error
 */
export const preFinalizePersistence =
    (
        comment: string | null = null,
        documentTypes: ArchiveDocumentType[] = [],
    ): RevaThunkAction<Promise<FinalizeValuationResponse | null>> =>
    async (dispatch, getState, extraArgument) => {
        const {serviceApi} = extraArgument;

        // #19370: nie eine AVM-Antwort einfrieren, die nicht zu den aktuellen Parametern gehört
        const indicationCurrency = await dispatch(ensureIndicationIsCurrent());
        if (indicationCurrency !== "current") {
            dispatch(setPreFinalizeError(outdatedIndicationQueryError(indicationCurrency)));
            return null;
        }

        const revaId = selectRevaId(getState());
        const segment = selectSegment(getState());
        const parametersInReva = selectAllParams(getState());
        const valuationParamsOut = selectIndicationData(getState());
        const valuationParamsIn = selectCurrentIndicationPayload(getState());
        const parametersInRegister = selectAllRegisterParams(getState());
        const parametersInEnergyCertificate = selectAllEnergyParams(getState());
        const parametersInIdpEnergyCertificate = selectAllIdpEnergyCertParams(getState());
        const parametersInPlot = selectAllPlotParams(getState());
        const parametersInInspectionDerived = selectAllInspectionDerivedParams(getState());
        const documentProperties = selectAllDocProperties(getState());

        if (!parametersInReva || !valuationParamsOut || !revaId) {
            return null;
        }

        const apiInterface = new PreFinalizeApiInterface(
            revaId,
            segment,
            parametersInReva,
            valuationParamsOut,
            valuationParamsIn,
            parametersInRegister,
            parametersInEnergyCertificate,
            parametersInIdpEnergyCertificate,
            parametersInPlot,
            parametersInInspectionDerived,
            documentProperties,
            comment,
            documentTypes,
        );

        if (!apiInterface.validateAndDispatchQueryError((queryError) => dispatch(setPreFinalizeError(queryError)))) {
            return null;
        }

        dispatch(setPreFinalizeLoading(true));

        const credentials = selectCredentials(getState());

        try {
            const response = await apiInterface.execute(serviceApi, credentials!);

            dispatch(handleValidPersistenceResponseReceived(response));
            dispatch(setPreFinalizeLoading(false));
            reloadDocumentsAfterArchiving(dispatch, response);

            return response;
        } catch (err) {
            dispatch(setPreFinalizeError(generateDefaultQueryError(err as Error)));
            return null;
        }
    };

/**
 * Async thunk to reopen the persistence.
 * @param comment comment for reopening
 */
export const reopenPersistence =
    (comment: string): RevaThunkAction<Promise<boolean>> =>
    async (dispatch, getState, extraArgument) => {
        const {serviceApi} = extraArgument;
        const revaId = selectRevaId(getState());
        const indicationData = selectFlattenedIndicationData(getState());
        const hasFirstFinalizeData = selectHasFirstFinalizeData(getState());
        const specColumns = selectVisibleObjectParameters(getState(), "object/overview", "all", indicationData);
        if (!revaId || !specColumns || !indicationData) {
            return false;
        }

        const firstFinalizeData = hasFirstFinalizeData ? null : getFirstFinalizeData(specColumns, indicationData);
        dispatch(setFinalizationLoading(true));
        const apiInterface = new PersistenceCorrectionApiInterface(revaId, comment, firstFinalizeData);
        const credentials = selectCredentials(getState());

        try {
            const response = await apiInterface.execute(serviceApi, credentials!);
            dispatch(handleValidPersistenceResponseReceived(response));
            return true;
        } catch {
            return false;
        }
    };

function getFirstFinalizeData(specColumns: any[], indicationData: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(
        specColumns
            .flatMap((column: any) => column.blocks?.flatMap((block: any) => block.rows))
            .filter((row: any) => row?.fixForCorrection === true)
            .map((parameter: any): [string, unknown] => [
                parameter.parameter,
                indicationData[parameter.associatedOutputApiParam],
            ]),
    );
}

/**
 * Schreibt den Segment-Override der Überblick-Schicht (#19444) — ein PATCH wie für jeden anderen
 * REVA-Parameter; `null` nimmt den Override zurück.
 *
 * Welche Schichten eine Segmentänderung verwirft, entscheidet das Backend, und es verwirft nur, wenn
 * sich das *effektive* Segment tatsächlich ändert. Die Antwort wird deshalb vollständig auf die
 * Parameterschichten angewendet, statt den Reset lokal zu erraten.
 * @param value das neue Segment, oder null zum Zurücksetzen des Overrides
 * @param comment optionaler Kommentar
 * @param segmentBefore das effektive Segment vor dem Schreibvorgang — die vom Segment abhängigen
 *        Dokument-Eigenschaften kennt das Backend nicht und werden nur bei einem echten Wechsel
 *        nachgezogen
 */
export const persistSegmentOverride =
    (value: string | null, comment: string | null, segmentBefore: string): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, extraArgument) => {
        const {serviceApi} = extraArgument;
        const revaId = selectRevaId(getState());

        const apiInterface = new PersistSingleParameterApiInterface("AVM", revaId, "segment", value, comment);

        if (
            !apiInterface.validateAndDispatchQueryError((queryError) =>
                dispatch(setPersistenceSingleParamError(queryError)),
            )
        ) {
            return;
        }

        dispatch(setPersistenceSingleParamLoading(true));
        const seq = nextPersistenceRequestSeq();
        dispatch(beginPersistenceWrite());
        dispatch(beginSegmentWrite());

        const credentials = selectCredentials(getState());

        try {
            const response = await apiInterface.execute(serviceApi, credentials!);

            // Von einer neueren Antwort überholt: nichts anwenden, sonst fällt der Store auf einen
            // älteren Vollstand zurück (gleicher Guard wie beim Laden).
            if (seq < selectLastAppliedResponseSeq(getState())) {
                dispatch(setPersistenceSingleParamLoading(false));
                return;
            }

            dispatch(setUpdatedValuationResponse({response, seq}));
            dispatch(applyValuationParametersFromResponse(response));
            if (response.notifications) {
                void dispatch(handleNotificationsFromPersistence(response.notifications));
            }
            dispatch(setPersistenceSingleParamLoading(false));

            if (response.segment !== segmentBefore) {
                // Dokument-Eigenschaften, die am Segment hängen: Die POI-Auswahl gehört zum alten
                // Objekttyp, und das Modell der Bruttogrundfläche gibt es in WHG_M/GRST_K nicht.
                void dispatch(persistSingleParameter(DOC_PROPERTY_POIS_PER_TYPE, "DOC_PROPERTY", null, null));
                if (response.segment === SEGMENT.WHG_M || response.segment === SEGMENT.GRST_K) {
                    dispatch(
                        setSingleParam({
                            parameter: DOC_PROPERTY_GROSS_FLOOR_AREA_MODEL,
                            parameterType: "DOC_PROPERTY",
                            value: null,
                        }),
                    );
                    void dispatch(
                        persistSingleParameter(DOC_PROPERTY_GROSS_FLOOR_AREA_MODEL, "DOC_PROPERTY", null, null),
                    );
                }
            }
        } catch (err) {
            if (!(err instanceof ErrorI18n) || err.message !== "object.error.missingComment") {
                void dispatch(resetPersistenceToLastValidResponse());
            }
            dispatch(setPersistenceSingleParamError(generateDefaultQueryError(err as Error)));
        } finally {
            dispatch(finishPersistenceWrite());
            dispatch(endSegmentWrite());
        }
    };

/**
 * Delete a valuation in Reva.
 * @param revaId
 */
export const deleteValuation =
    (revaId: string): RevaThunkAction<Promise<DeleteValuationResponse | null>> =>
    async (dispatch, getState, extraArgument) => {
        const {serviceApi} = extraArgument;
        const credentials = selectCredentials(getState());

        try {
            return await new DeleteValuationApiInterface(revaId).execute(serviceApi, credentials!);
        } catch (err) {
            dispatch(setQueryError(generateDefaultQueryError(err as Error)));
            return null;
        }
    };

/**
 * Delete a valuation in Reva and also delete the order from Value-Orders-API.
 * @param revaId
 * @param reason
 * @param explanation
 */
export const deleteValuationAndOrder =
    (revaId: string, reason: string, explanation: string): RevaThunkAction<Promise<DeleteValuationResponse | null>> =>
    async (dispatch, getState, extraArgument) => {
        const {serviceApi} = extraArgument;
        const credentials = selectCredentials(getState());

        try {
            return await new DeleteValuationAndOrderApiInterface(revaId, reason, explanation).execute(
                serviceApi,
                credentials!,
            );
        } catch (err) {
            dispatch(setQueryError(generateDefaultQueryError(err as Error)));
            return null;
        }
    };

/**
 * Load the assignable users for the selected valuation
 * @param revaId revaId of the selected valuation
 */
export const loadAssignableUsers =
    (revaId?: string | null): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, extraArgument) => {
        if (!revaId) {
            return;
        }
        const {env, serviceApi} = extraArgument;
        const state = getState();
        const credentials = selectCredentials(state);
        dispatch(setAssignableUsersLoading(true));
        try {
            const response = await new SearchAssignableUsersApiInterface(revaId).execute(serviceApi, credentials!);
            if (env.isDevelopmentMode) console.log(`Assignable owners loaded:`, response);
            dispatch(setAssignableUsers(response));
        } catch (err) {
            console.error("Error loading assignable owners", err);
            dispatch(setAssignableUsers([]));
        } finally {
            dispatch(setAssignableUsersLoading(false));
        }
    };

/**
 * Load the finalize-capable users a valuation may be handed to as its new owner when reopening it for a
 * correction (snapshot authors of this valuation first). Returns the list directly (not stored in the slice) —
 * the correction dialogs hold it in local state. Requires reva-ownership-assign on the backend; returns an empty
 * list on error (e.g. when the user lacks that right — the dialogs only offer the pool then).
 * @param revaId revaId of the valuation to be reopened
 */
export const loadFinalizeAssignableUsers =
    (revaId?: string | null): RevaThunkAction<Promise<AssignableUser[]>> =>
    async (_dispatch, getState, extraArgument) => {
        if (!revaId) {
            return [];
        }
        const {serviceApi} = extraArgument;
        const credentials = selectCredentials(getState());
        try {
            return await new SearchFinalizeAssignableUsersApiInterface(revaId).execute(serviceApi, credentials!);
        } catch (err) {
            console.error("Error loading finalize-assignable users", err);
            return [];
        }
    };

/**
 * Assign the valuation to a user.
 * @param revaId revaId of the valuation to assign
 * @param username the new owner, or null to hand the valuation back to the pool
 */
export const assignValuation =
    (revaId: string, username: string | null): RevaThunkAction<Promise<ValuationData | null>> =>
    async (dispatch, getState, extraArgument) => {
        if (!revaId) {
            return null;
        }
        const {serviceApi} = extraArgument;
        const state = getState();
        const credentials = selectCredentials(state);
        // If a snapshot is loaded, tell the backend so it frames the response as that snapshot instead
        // of switching the client to the live state (null when live → query param is omitted).
        const snapshotNumber = selectLoadedSnapshotNumber(state);
        const seq = nextPersistenceRequestSeq();
        dispatch(beginPersistenceWrite());
        try {
            const response = await new AssignUserApiInterface(revaId, username, snapshotNumber).execute(
                serviceApi,
                credentials!,
            );
            dispatch(setUpdatedValuationResponse({response, seq}));
            return response;
        } catch (err) {
            console.error("Error assigning valuation", err);
            // Strukturierte Backend-Meldung (ErrorI18n mit errorData) erhalten, damit der Aufrufer die konkrete
            // Fehlermeldung (error_de) anzeigen kann; nur bei unerwarteten Fehlern generisch.
            throw err instanceof ErrorI18n ? err : new ErrorI18n("general.error.invalid");
        } finally {
            dispatch(finishPersistenceWrite());
        }
    };

/**
 * #19481: Zweckwechsel der geladenen REVA-ID, z. B. Research → Bewertung. Die Antwort trägt den kompletten neuen
 * Stand (wie ein Laden) und ersetzt die Metadaten im Store, sodass die Ansicht sofort die Möglichkeiten des neuen
 * Zwecks zeigt (Reiter „Auftrag", Finalisieren, PDFs).
 * @param revaId die REVA-ID
 * @param purpose Zielzweck
 * @throws ErrorI18n mit der Backend-Meldung (403 ohne Recht, 409 bei nicht erlaubtem Wechsel)
 */
export const changeValuationPurpose =
    (revaId: string, purpose: RevaValuationPurpose): RevaThunkAction<Promise<ValuationData>> =>
    async (dispatch, getState, extraArgument) => {
        const {serviceApi} = extraArgument;
        const credentials = selectCredentials(getState());
        const seq = nextPersistenceRequestSeq();
        dispatch(beginPersistenceWrite());
        try {
            const response = await new ChangePurposeApiInterface(revaId, purpose).execute(serviceApi, credentials!);
            dispatch(setUpdatedValuationResponse({response, seq}));
            return response;
        } catch (err) {
            console.error("Error changing valuation purpose", err);
            throw err instanceof ErrorI18n ? err : new ErrorI18n("general.error.invalid");
        } finally {
            dispatch(finishPersistenceWrite());
        }
    };

/**
 * Gespeicherte Kartenansicht: Mittelpunkt in WGS84 und Zoomstufe. Liegt je Karte als technische
 * documentProperty unter einem eigenen Schlüssel (z.B. "overview_map_settings").
 */
export interface MapSettings extends ILatLonCoordinate {
    /** Zoomstufe der Karte, auch gebrochen - OpenLayers kennt Zwischenstufen. */
    zoom: number;
}

/**
 * Silently persist map settings (zoom, lat, lon) to documentProperties with ?technical=true.
 * @param mapKey the documentProperties key (e.g. "overview_map_settings")
 * @param settings map view settings
 */
export const persistMapSettings =
    (mapKey: string, settings: MapSettings): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, extraArgument) => {
        const {serviceApi} = extraArgument;
        const revaId = selectRevaId(getState());
        if (!revaId) {
            return;
        }

        const credentials = selectCredentials(getState());

        dispatch(setSingleParam({parameter: mapKey, parameterType: "DOC_PROPERTY", value: settings}));

        // If a snapshot is loaded, tell the backend so it frames the response as that snapshot instead
        // of switching the client to the live state (null when live → query param is omitted).
        const snapshotNumber = selectLoadedSnapshotNumber(getState());

        // Persisted as a technical document property; keeps its own silent error handling (no loading
        // flag, no state reset) so a failed background save never disturbs the user's edits.
        const apiInterface = new PersistSingleDocumentPropertyApiInterface(
            revaId,
            mapKey,
            settings,
            true,
            snapshotNumber,
        );

        // Zählt trotz "silent" als Schreibvorgang: die Antwort trägt den kompletten Stand, und
        // solange sie unterwegs ist, hinkt lastValidPersistenceResponse hinterher.
        const seq = nextPersistenceRequestSeq();
        dispatch(beginPersistenceWrite());
        try {
            const response = await apiInterface.execute(serviceApi, credentials!);
            dispatch(setUpdatedValuationResponse({response, seq}));
        } catch (err) {
            console.error(`Error persisting map settings for ${mapKey}`, err);
        } finally {
            dispatch(finishPersistenceWrite());
        }
    };
